import { useEffect, useRef, useState } from 'react';
import { searchApi } from '../lib/api';
import type { GlobalSearchResults } from '../types';

const DEBOUNCE_MS = 200;
const MIN_CHARS = 2;

interface UseGlobalSearchReturn {
  query: string;
  setQuery: (q: string) => void;
  results: GlobalSearchResults;
  counts: Partial<Record<'people' | 'organizations' | 'tasks', number>>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Owns the debounced `/api/search` call and stale-response handling.
 * The component is responsible for rendering states, not for fetching.
 */
export function useGlobalSearch(): UseGlobalSearchReturn {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchResults>({});
  const [counts, setCounts] = useState<UseGlobalSearchReturn['counts']>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const trimmed = query.trim();

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    if (trimmed.length < MIN_CHARS) {
      setResults({});
      setCounts({});
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    debounceRef.current = setTimeout(() => {
      const controller = new AbortController();
      abortRef.current = controller;
      const myRequestId = ++requestIdRef.current;

      searchApi
        .search(trimmed, undefined, controller.signal)
        .then((response) => {
          if (myRequestId !== requestIdRef.current) return; // stale
          setResults(response.results);
          setCounts(response.counts);
          setError(null);
          setIsLoading(false);
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          if (myRequestId !== requestIdRef.current) return;
          setError(err?.message ?? 'Search failed');
          setIsLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [query]);

  return { query, setQuery, results, counts, isLoading, error };
}
